// ============================================================================
// VIRTUALIZED TABLE COMPONENT
// 
// Uses react-window for efficient rendering of large datasets
// Only renders visible rows for smooth scrolling with 1k-10k rows
// 
// Accessibility:
// - Proper ARIA roles and labels
// - Keyboard navigation support
// - Screen reader friendly
// ============================================================================

import React, { useCallback, useRef, useEffect } from 'react';
import { FixedSizeList as List } from 'react-window';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';

// ============================================================================
// PAGINATION CONTROLS
// ============================================================================

export const PaginationControls = ({
  currentPage,
  totalPages,
  hasMore,
  hasPrev,
  onNextPage,
  onPrevPage,
  onFirstPage,
  onLastPage,
  pageSize,
  totalItems,
  loading = false,
}) => {
  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems || currentPage * pageSize);

  return (
    <div 
      className="flex items-center justify-between px-4 py-3 bg-slate-900/50 border-t border-slate-700/50"
      role="navigation"
      aria-label="Pagination"
    >
      <div className="text-sm text-slate-400">
        {totalItems ? (
          <span>Showing <span className="font-medium text-white">{startItem}</span> to <span className="font-medium text-white">{endItem}</span> of <span className="font-medium text-white">{totalItems}</span></span>
        ) : (
          <span>Page <span className="font-medium text-white">{currentPage}</span></span>
        )}
      </div>
      
      <div className="flex items-center gap-1">
        <button
          onClick={onFirstPage}
          disabled={!hasPrev || loading}
          className="p-2 rounded-lg hover:bg-slate-700/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          aria-label="First page"
          title="First page"
        >
          <ChevronsLeft className="w-4 h-4 text-slate-400" />
        </button>
        <button
          onClick={onPrevPage}
          disabled={!hasPrev || loading}
          className="p-2 rounded-lg hover:bg-slate-700/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          aria-label="Previous page"
          title="Previous page"
        >
          <ChevronLeft className="w-4 h-4 text-slate-400" />
        </button>
        
        <span className="px-3 py-1 text-sm text-slate-300">
          {currentPage}{totalPages ? ` / ${totalPages}` : ''}
        </span>
        
        <button
          onClick={onNextPage}
          disabled={!hasMore || loading}
          className="p-2 rounded-lg hover:bg-slate-700/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          aria-label="Next page"
          title="Next page"
        >
          <ChevronRight className="w-4 h-4 text-slate-400" />
        </button>
        <button
          onClick={onLastPage}
          disabled={!hasMore || loading || !totalPages}
          className="p-2 rounded-lg hover:bg-slate-700/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          aria-label="Last page"
          title="Last page"
        >
          <ChevronsRight className="w-4 h-4 text-slate-400" />
        </button>
      </div>
    </div>
  );
};

// ============================================================================
// VIRTUALIZED TABLE
// ============================================================================

const VirtualizedTable = ({
  columns,
  data,
  rowHeight = 56,
  headerHeight = 48,
  maxHeight = 500,
  loading = false,
  emptyMessage = 'No data available',
  onRowClick,
  selectedRowId,
  getRowId = (row, index) => row.id || row.eventId || index,
  // Pagination props
  pagination,
  className = '',
}) => {
  const listRef = useRef(null);
  const headerRef = useRef(null);

  // Calculate visible height
  const tableHeight = Math.min(data.length * rowHeight, maxHeight);

  // Sync horizontal scroll between header and body
  const handleScroll = useCallback(({ scrollLeft }) => {
    if (headerRef.current) {
      headerRef.current.scrollLeft = scrollLeft;
    }
  }, []);

  // Row renderer
  const Row = useCallback(({ index, style }) => {
    const row = data[index];
    const rowId = getRowId(row, index);
    const isSelected = selectedRowId === rowId;

    return (
      <div
        style={style}
        className={`flex items-center border-b border-slate-700/30 hover:bg-slate-800/50 transition-colors cursor-pointer ${
          isSelected ? 'bg-emerald-500/10' : ''
        }`}
        onClick={() => onRowClick?.(row, index)}
        role="row"
        aria-selected={isSelected}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onRowClick?.(row, index);
          }
        }}
      >
        {columns.map((col, colIndex) => (
          <div
            key={col.key || colIndex}
            className={`px-4 py-3 text-sm ${col.className || ''}`}
            style={{ width: col.width || 'auto', flex: col.flex || 1 }}
            role="cell"
          >
            {col.render ? col.render(row[col.key], row, index) : row[col.key]}
          </div>
        ))}
      </div>
    );
  }, [data, columns, onRowClick, selectedRowId, getRowId]);

  // Empty state
  if (!loading && data.length === 0) {
    return (
      <div className={`bg-slate-800/40 border border-slate-700/50 rounded-2xl overflow-hidden ${className}`}>
        {/* Header */}
        <div 
          className="flex items-center bg-slate-900/50 border-b border-slate-700/50"
          style={{ height: headerHeight }}
          role="row"
        >
          {columns.map((col, index) => (
            <div
              key={col.key || index}
              className={`px-4 py-3 text-xs font-medium text-slate-500 uppercase ${col.headerClassName || ''}`}
              style={{ width: col.width || 'auto', flex: col.flex || 1 }}
              role="columnheader"
            >
              {col.title}
            </div>
          ))}
        </div>
        
        {/* Empty message */}
        <div 
          className="flex items-center justify-center text-slate-500 py-12"
          role="row"
        >
          {emptyMessage}
        </div>
      </div>
    );
  }

  return (
    <div 
      className={`bg-slate-800/40 border border-slate-700/50 rounded-2xl overflow-hidden ${className}`}
      role="table"
      aria-label="Data table"
      aria-rowcount={data.length}
    >
      {/* Fixed Header */}
      <div
        ref={headerRef}
        className="flex items-center bg-slate-900/50 border-b border-slate-700/50 overflow-hidden"
        style={{ height: headerHeight }}
        role="row"
      >
        {columns.map((col, index) => (
          <div
            key={col.key || index}
            className={`px-4 py-3 text-xs font-medium text-slate-500 uppercase ${col.headerClassName || ''}`}
            style={{ width: col.width || 'auto', flex: col.flex || 1 }}
            role="columnheader"
            aria-sort={col.sorted || 'none'}
          >
            {col.title}
          </div>
        ))}
      </div>

      {/* Virtualized Body */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full" />
        </div>
      ) : (
        <List
          ref={listRef}
          height={tableHeight}
          itemCount={data.length}
          itemSize={rowHeight}
          width="100%"
          onScroll={handleScroll}
          role="rowgroup"
        >
          {Row}
        </List>
      )}

      {/* Pagination */}
      {pagination && (
        <PaginationControls {...pagination} />
      )}
    </div>
  );
};

// ============================================================================
// RESPONSIVE TABLE (Mobile: Cards, Desktop: Table)
// ============================================================================

export const ResponsiveTable = ({
  columns,
  data,
  loading = false,
  emptyMessage = 'No data available',
  onRowClick,
  selectedRowId,
  getRowId = (row, index) => row.id || row.eventId || index,
  pagination,
  mobileBreakpoint = 768,
  className = '',
}) => {
  const [isMobile, setIsMobile] = React.useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < mobileBreakpoint);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, [mobileBreakpoint]);

  // Mobile: Stacked cards
  if (isMobile) {
    return (
      <div className={`space-y-3 ${className}`}>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full" />
          </div>
        ) : data.length === 0 ? (
          <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-6 text-center text-slate-500">
            {emptyMessage}
          </div>
        ) : (
          <>
            {data.map((row, index) => {
              const rowId = getRowId(row, index);
              const isSelected = selectedRowId === rowId;

              return (
                <div
                  key={rowId}
                  className={`bg-slate-800/40 border border-slate-700/50 rounded-xl p-4 ${
                    isSelected ? 'ring-2 ring-emerald-500/50' : ''
                  }`}
                  onClick={() => onRowClick?.(row, index)}
                  role="article"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onRowClick?.(row, index);
                    }
                  }}
                >
                  <div className="space-y-2">
                    {columns.map((col, colIndex) => (
                      <div key={col.key || colIndex} className="flex justify-between items-start gap-2">
                        <span className="text-xs font-medium text-slate-500 uppercase">
                          {col.title}
                        </span>
                        <span className={`text-sm text-right ${col.className || 'text-white'}`}>
                          {col.render ? col.render(row[col.key], row, index) : row[col.key]}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
            
            {/* Pagination for mobile */}
            {pagination && (
              <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl overflow-hidden">
                <PaginationControls {...pagination} />
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  // Desktop: Virtualized table
  return (
    <VirtualizedTable
      columns={columns}
      data={data}
      loading={loading}
      emptyMessage={emptyMessage}
      onRowClick={onRowClick}
      selectedRowId={selectedRowId}
      getRowId={getRowId}
      pagination={pagination}
      className={className}
    />
  );
};

export default VirtualizedTable;
